import {
  getNoCacheHeaders,
  getValidNonce,
  invalidateNonceCache,
} from './nonce';

export interface ModelSearchRow {
  id: number;
  name: string;
  slug: string;
  url: string;
  count: number;
}

interface WpJson<T> {
  success: boolean;
  data?: T & { nonce_error?: boolean; message?: string };
}

export async function fetchModelSearch(query: string): Promise<ModelSearchRow[]> {
  const ajax = window.rnmAjax?.ajaxurl;
  if (!ajax) {
    throw new Error('rnmAjax.ajaxurl no está definido');
  }

  const run = async (): Promise<WpJson<{ results?: ModelSearchRow[] }>> => {
    const nonce = await getValidNonce('models');
    const url =
      ajax +
      '?action=rnm_search_models&nonce=' +
      encodeURIComponent(nonce) +
      '&q=' +
      encodeURIComponent(query);

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: getNoCacheHeaders(),
    });
    return response.json() as Promise<WpJson<{ results?: ModelSearchRow[] }>>;
  };

  let data = await run();

  if (!data.success && data.data?.nonce_error) {
    console.warn(
      '⚠️ Nonce rechazado por el servidor (modelos), invalidando cache y reintentando...'
    );
    invalidateNonceCache('models');
    data = await run();
    if (!data.success && data.data?.nonce_error) {
      console.error('❌ Error de nonce persistente (modelos), recargando página...');
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      throw new Error('nonce_models');
    }
  }

  if (!data.success || !data.data?.results) {
    return [];
  }
  return data.data.results;
}

export interface GlobalProduct {
  id: number;
  title: string;
  url: string;
  price: string;
  image: string;
  sku: string;
  in_stock: boolean;
}

export interface GlobalPost {
  id: number;
  title: string;
  url: string;
  excerpt: string;
  date: string;
}

export interface GlobalPage {
  id: number;
  title: string;
  url: string;
  excerpt: string;
}

export interface GlobalSearchData {
  results: {
    products: GlobalProduct[];
    posts: GlobalPost[];
    pages: GlobalPage[];
  };
  total: number;
  query: string;
}

interface GlobalWpData extends GlobalSearchData {
  nonce_error?: boolean;
  message?: string;
}

export async function fetchGlobalSearch(
  query: string,
  options?: { reloadOnPersistentNonce?: boolean }
): Promise<{ ok: true; data: GlobalSearchData } | { ok: false; message: string }> {
  const reloadOnPersistentNonce = options?.reloadOnPersistentNonce !== false;
  const ajax = window.rnmAjax?.ajaxurl;
  if (!ajax) {
    throw new Error('rnmAjax.ajaxurl no está definido');
  }

  const postOnce = async (nonce: string) => {
    const formData = new FormData();
    formData.append('action', 'rnm_global_search');
    formData.append('nonce', nonce);
    formData.append('q', query);
    const response = await fetch(ajax, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: getNoCacheHeaders(),
    });
    return response.json() as Promise<WpJson<GlobalWpData>>;
  };

  let nonce = await getValidNonce('global');
  let data = await postOnce(nonce);

  if (!data.success && data.data?.nonce_error) {
    console.warn(
      '⚠️ Nonce rechazado por el servidor (búsqueda global), invalidando cache y reintentando...'
    );
    invalidateNonceCache('global');
    nonce = await getValidNonce('global');
    data = await postOnce(nonce);
    if (!data.success && data.data?.nonce_error) {
      console.error('❌ Error de nonce persistente (global)');
      if (reloadOnPersistentNonce) {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return {
          ok: false,
          message: 'Sesión expirada. Recargando...',
        };
      }
      return {
        ok: false,
        message: 'Error de sesión. Por favor recarga la página.',
      };
    }
  }

  if (!data.success) {
    const msg =
      data.data?.message ||
      'No se encontraron resultados para "<strong>' +
        escapeHtml(query) +
        '</strong>"';
    return { ok: false, message: msg };
  }

  if (!data.data || data.data.total <= 0) {
    const msg =
      data.data?.message ||
      'No se encontraron resultados para "<strong>' +
        escapeHtml(query) +
        '</strong>"';
    return { ok: false, message: msg };
  }

  return { ok: true, data: data.data as GlobalSearchData };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
